import { useEffect, useRef, useState } from 'preact/hooks';
import { api } from '../api.ts';
import { bump, changed } from '../signal.ts';
import { show, showError } from '../toast.ts';
import {
  duration, fromKg, kcal, localTime, mealNow, MEAL_LABEL, prettyDate, shiftDate, today, toKg, weight, weightUnit,
} from '../format.ts';
import { MEALS, type AiLogResult, type DaySummary, type Entry, type Meal, type Settings } from '../../shared/types.ts';
import { AddSheet } from './AddSheet.tsx';
import { EntrySheet } from './EntrySheet.tsx';
import { prepareImage } from '../image.ts';

const METHOD_LABEL: Record<string, string> = {
  strava: 'Strava estimate',
  power: 'from power',
  hr: 'from heart rate',
  distance: 'from distance',
  met: 'typical for the sport',
};

export function Today({ settings, ai }: { settings: Settings; ai: boolean }) {
  const [date, setDate] = useState(today);
  const [day, setDay] = useState<DaySummary | null>(null);
  const [adding, setAdding] = useState<Meal | null>(null);
  const [editing, setEditing] = useState<Entry | null>(null);
  const tick = changed.use();

  useEffect(() => {
    let live = true;
    api<DaySummary>(`/api/day?date=${date}`)
      .then((d) => live && setDay(d))
      .catch(showError);
    return () => void (live = false);
  }, [date, tick]);

  // Coming back to the tab on a new day should land on that day.
  useEffect(() => {
    let last = today();
    const on = () => {
      if (document.visibilityState !== 'visible') return;
      const t = today();
      if (t !== last) {
        setDate((d) => (d === last ? t : d));
        last = t;
      }
      bump();
    };
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  }, []);

  return (
    <div class="today">
      <header class="datebar">
        <button class="icon" aria-label="Previous day" onClick={() => setDate(shiftDate(date, -1))}>‹</button>
        <button class="datelabel" onClick={() => setDate(today())}>{prettyDate(date)}</button>
        <button class="icon" aria-label="Next day" onClick={() => setDate(shiftDate(date, 1))}>›</button>
      </header>

      {day && <Summary day={day} />}

      <Composer date={date} ai={ai} />

      {day && (
        <>
          {MEALS.map((m) => (
            <MealCard key={m} meal={m} entries={day.entries.filter((e) => e.meal === m)} onAdd={() => setAdding(m)} onEdit={setEditing} />
          ))}
          <Activities day={day} />
          <WeightCard day={day} settings={settings} />
        </>
      )}

      {adding && <AddSheet date={date} meal={adding} onClose={() => setAdding(null)} />}
      {editing && <EntrySheet entry={editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function Summary({ day }: { day: DaySummary }) {
  const { targets: t, totals } = day;
  const left = t.kcal - totals.kcal;
  const pct = Math.min(100, (totals.kcal / t.kcal) * 100);
  const goal = t.kcal - t.expenditure;
  return (
    <section class="card summary">
      <div class="hero">
        <div>
          <div class="big">{kcal(Math.abs(left))}</div>
          <div class="muted">{left >= 0 ? 'kcal left' : 'kcal over'}</div>
        </div>
        <div class="right muted">
          <div><strong>{kcal(totals.kcal)}</strong> eaten</div>
          <div><strong>{kcal(t.kcal)}</strong> target</div>
        </div>
      </div>
      <div class="bar" role="img" aria-label={`${Math.round(pct)}% of calorie target`}>
        <span class={left < 0 ? 'over' : ''} style={{ width: `${pct}%` }} />
      </div>
      <div class="macros">
        <Macro label="Protein" cls="p" got={totals.protein} want={t.protein} />
        <Macro label="Carbs" cls="c" got={totals.carbs} want={t.carbs} />
        <Macro label="Fat" cls="f" got={totals.fat} want={t.fat} />
      </div>
      <p class="breakdown muted">
        {kcal(t.baseline)} baseline
        {t.exercise > 0 && <> + {kcal(t.exercise)} exercise</>}
        {goal !== 0 && <> {goal < 0 ? '−' : '+'} {kcal(Math.abs(goal))} {goal < 0 ? 'deficit' : 'surplus'}</>}
      </p>
    </section>
  );
}

function Macro({ label, cls, got, want }: { label: string; cls: string; got: number; want: number }) {
  const pct = want > 0 ? Math.min(100, (got / want) * 100) : 0;
  return (
    <div class="macro">
      <div class="row">
        <span>{label}</span>
        <span class="muted">{Math.round(got)} / {Math.round(want)} g</span>
      </div>
      <div class={`bar thin ${cls}`}>
        <span style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function Composer({ date, ai }: { date: string; ai: boolean }) {
  const [text, setText] = useState('');
  const [image, setImage] = useState<{ mime: string; data: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  if (!ai) {
    return (
      <section class="card composer off">
        <p class="muted">AI logging is off: the server has no Anthropic API key.</p>
      </section>
    );
  }

  async function submit(e: Event) {
    e.preventDefault();
    if (busy || (!text.trim() && !image)) return;
    setBusy(true);
    try {
      const r = await api<AiLogResult>('/api/ai/log', {
        body: {
          text: text.trim(),
          image: image && { mime: image.mime, data: image.data },
          date,
          meal: mealNow(),
          localTime: localTime(),
        },
      });
      setText('');
      setImage(null);
      bump();
      const ids = r.entries.map((x) => x.id);
      const total = r.entries.reduce((t, x) => t + x.kcal, 0);
      if (!ids.length) show(r.note || 'Nothing to log there.');
      else
        show(`Logged ${ids.length} item${ids.length > 1 ? 's' : ''}, ${kcal(total)} kcal${r.note ? ` · ${r.note}` : ''}`, {
          ms: 12000,
          action: {
            label: 'Undo',
            run: () => api('/api/entries/delete', { body: { ids } }).then(bump, showError),
          },
        });
    } catch (err) {
      showError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form class="card composer" onSubmit={submit}>
      <textarea
        rows={2}
        value={text}
        placeholder="What did you eat? e.g. “2 eggs, toast with butter, oat flat white”"
        onInput={(e) => setText(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) submit(e);
        }}
        disabled={busy}
      />
      {image && (
        <div class="thumb">
          <img src={image.url} alt="Attached photo" />
          <button type="button" class="link" onClick={() => setImage(null)}>Remove</button>
        </div>
      )}
      <div class="row">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={async (e) => {
            const f = e.currentTarget.files?.[0];
            e.currentTarget.value = '';
            if (f) setImage(await prepareImage(f).catch((err) => (showError(err), null)));
          }}
        />
        <button type="button" class="ghost" onClick={() => fileRef.current?.click()} disabled={busy}>
          Add photo
        </button>
        <button class="primary" disabled={busy || (!text.trim() && !image)}>{busy ? 'Logging…' : 'Log it'}</button>
      </div>
    </form>
  );
}

function MealCard({ meal, entries, onAdd, onEdit }: {
  meal: Meal; entries: Entry[]; onAdd: () => void; onEdit: (e: Entry) => void;
}) {
  const total = entries.reduce((t, e) => t + e.kcal, 0);
  return (
    <section class="card meal">
      <header class="row">
        <h2>{MEAL_LABEL[meal]}</h2>
        <span class="muted">{entries.length ? `${kcal(total)} kcal` : ''}</span>
      </header>
      {entries.map((e) => (
        <button key={e.id} class="entry" onClick={() => onEdit(e)}>
          <span class="name">
            {e.name}
            <small class="muted">{e.amount}</small>
          </span>
          <span class="nums">
            {kcal(e.kcal)}
            <small class="muted">P{Math.round(e.protein)} C{Math.round(e.carbs)} F{Math.round(e.fat)}</small>
          </span>
        </button>
      ))}
      <button class="link add" onClick={onAdd}>+ Add food</button>
    </section>
  );
}

function Activities({ day }: { day: DaySummary }) {
  if (!day.activities.length) return null;
  return (
    <section class="card">
      <header class="row">
        <h2>Exercise</h2>
        <span class="muted">{kcal(day.targets.exercise)} kcal</span>
      </header>
      {day.activities.map((a) => (
        <div key={a.id} class={`activity ${a.duplicateOf ? 'dup' : ''}`}>
          <span class="name">
            {a.name}
            <small class="muted">
              {a.sport} · {duration(a.movingS)}
              {a.distanceM ? ` · ${(a.distanceM / 1000).toFixed(1)} km` : ''}
            </small>
          </span>
          <span class="nums">
            {a.duplicateOf ? '—' : kcal(a.kcal)}
            <small class="muted">{a.duplicateOf ? 'duplicate recording' : METHOD_LABEL[a.method]}</small>
          </span>
        </div>
      ))}
    </section>
  );
}

function WeightCard({ day, settings }: { day: DaySummary; settings: Settings }) {
  const [v, setV] = useState('');
  useEffect(() => setV(day.weight ? fromKg(day.weight.kg, settings).toFixed(1) : ''), [day.weight?.kg, settings.units]);

  async function save(e: Event) {
    e.preventDefault();
    const n = Number(v.replace(',', '.'));
    const kg = v.trim() === '' ? null : toKg(n, settings);
    if (kg !== null && !(n > 0)) return showError('Enter a weight');
    try {
      await api(`/api/weights/${day.date}`, { method: 'PUT', body: { kg } });
      bump();
      show(kg === null ? 'Weight cleared' : 'Weight saved');
    } catch (err) {
      showError(err);
    }
  }

  return (
    <form class="card weight row" onSubmit={save}>
      <label>
        <h2>Weight</h2>
        <small class="muted">Trend {weight(day.trendKg, settings)}</small>
      </label>
      <span class="inputs">
        <input inputMode="decimal" value={v} placeholder="—" onInput={(e) => setV(e.currentTarget.value)} aria-label="Scale weight" />
        <span class="muted">{weightUnit(settings)}</span>
        <button class="ghost">Save</button>
      </span>
    </form>
  );
}
